import { ChargeControl } from '../../components/ChargeControl';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import { Icon } from '../../components/Icon';
import { PeriodicTableIcon } from '../../components/PeriodicTableIcon';
import { BondsGroup } from './BondsGroup';
import { FunctionalGroupsGroup } from './FunctionalGroupsGroup';
import { CyclesGroup } from './CyclesGroup';
import { ShapesGroup } from './ShapesGroup';
import { CalloutsGroup } from './CalloutsGroup';
import { ReactionArrowsGroup } from './ReactionArrowsGroup';
import { TemplatesRail } from './TemplatesRail';
import { RailIconStrip, railGroupDomId, type RailGroupId } from './RailIconStrip';
import { RGroupToolGroup } from './RGroupTool';

// #413 / #415 -- the rail disclosures form a single-open accordion. Bonds is the one open at
// launch; Templates too is a disclosure now, closed at launch.
type DisclosureGroupId =
  'bonds' | 'cycles' | 'groups' | 'markush' | 'reactionArrows' | 'shapes' | 'callouts' | 'templates';

interface ToolRailProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  readonly doc: UseDrawingDocumentResult;
  readonly charge: number;
  readonly chargeLive: boolean;
  readonly onChargeChange: (value: number) => void;
  readonly onOpenPeriodicTable: () => void;
  // #654 -- opens the periodic table in multi-select mode for a Markush atom list.
  readonly onOpenAtomList?: () => void;
  // #656 -- the repeat-unit button's state and action.
  readonly bracket?: { readonly enabled: boolean; readonly onBracket: () => void };
  readonly onLoadTemplate: (id: string) => void;
  readonly onLoadMoleculeTemplate: (id: string) => void;
  readonly templateLoadDisabled: boolean;
  // #400 — the collapsed icon-strip state (localStorage-backed, viewport-gated in the shell).
  readonly collapsed: boolean;
  readonly setCollapsed: (collapsed: boolean) => void;
}

// ADR 0007 P2/P9 — the left rail, top to bottom: pointer modes (draw / select / periodic table),
// the Bonds group (#249 — the Chain draw mode plus armed bond order / stereo), the Cycles group
// (#252 — ring templates), the Markush group (#674 — R labels, atom list, repeat unit), the
// Reaction arrows and Shapes groups (#258 R5.3 — canvas annotations:
// scheme + mechanism arrows, decorative shapes), and the Templates favourites shelf (P9 / #415).
// History actions live in the action bar (P4), file actions in the File drawer (P8).
// #400 — the rail collapses to `RailIconStrip` on desktop; the shell owns the collapsed state.
// #413 / #415 — the disclosures are a single-open accordion; Bonds opens at launch.
export function ToolRail({
  tool,
  setTool,
  doc,
  charge,
  chargeLive,
  onChargeChange,
  onOpenPeriodicTable,
  onOpenAtomList,
  bracket,
  onLoadTemplate,
  onLoadMoleculeTemplate,
  templateLoadDisabled,
  collapsed,
  setCollapsed
}: ToolRailProps) {
  const { t } = useTranslation();
  const armedZ = tool.startsWith('element-') ? Number(tool.slice('element-'.length)) : null;

  // #413 -- at most one disclosure open at a time; Bonds is open at launch.
  const [openGroup, setOpenGroup] = useState<DisclosureGroupId | null>('bonds');
  const toggleGroup = useCallback(
    (id: DisclosureGroupId) => setOpenGroup((current) => (current === id ? null : id)),
    []
  );

  const expandToGroup = useCallback(
    (id: RailGroupId) => {
      setCollapsed(false);
      setOpenGroup(id);
      // Wait for the expanded layout to mount before scrolling to the group.
      requestAnimationFrame(() => {
        document.getElementById(railGroupDomId(id))?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    },
    [setCollapsed]
  );

  if (collapsed) {
    return (
      <aside
        className="workspace-rail workspace-rail--collapsed"
        data-testid="tool-rail"
        aria-label={t('workspace.rail.label')}
      >
        <RailIconStrip
          tool={tool}
          onOpenPeriodicTable={onOpenPeriodicTable}
          onExpand={() => setCollapsed(false)}
          onExpandToGroup={expandToGroup}
        />
      </aside>
    );
  }

  return (
    <aside className="workspace-rail" data-testid="tool-rail" aria-label={t('workspace.rail.label')}>
      <div className="workspace-rail-group" role="group" aria-label={t('drawingToolNav.chemicalToolsGroupAriaLabel')}>
        {/* #807 part 1b -- Draw / Select / Pan moved to the paper toolbar's pointer-mode group. */}
        {/* #676 -- the collapse control shares the Elements row instead of a header row of its own;
            it stays at the rail's inner (right) edge. (Beside Draw/Select it cut their labels off
            at desktop rail width.) */}
        <div className="workspace-rail-top-row">
          <button
            type="button"
            className={`button-with-icon ${armedZ !== null ? 'active' : ''}`}
            aria-pressed={armedZ !== null}
            aria-label={
              armedZ !== null
                ? t('workspace.rail.periodicTable.armed', { symbol: elementSymbol(armedZ) })
                : t('workspace.rail.periodicTable.open')
            }
            title={
              armedZ !== null
                ? t('workspace.rail.periodicTable.armed', { symbol: elementSymbol(armedZ) })
                : t('workspace.rail.periodicTable.open')
            }
            onClick={onOpenPeriodicTable}
          >
            <PeriodicTableIcon />
            <span>{armedZ !== null ? elementSymbol(armedZ) : t('workspace.rail.periodicTable.label')}</span>
          </button>
          <button
            type="button"
            className="icon-button workspace-rail-toggle"
            aria-label={t('workspace.rail.collapse')}
            title={t('workspace.rail.collapse')}
            onClick={() => setCollapsed(true)}
          >
            <Icon name="chevronLeft" />
          </button>
        </div>
        <ChargeControl value={charge} onChange={onChargeChange} live={chargeLive} />
      </div>
      <BondsGroup doc={doc} open={openGroup === 'bonds'} onToggle={() => toggleGroup('bonds')} />
      <CyclesGroup tool={tool} setTool={setTool} open={openGroup === 'cycles'} onToggle={() => toggleGroup('cycles')} />
      <FunctionalGroupsGroup
        tool={tool}
        setTool={setTool}
        open={openGroup === 'groups'}
        onToggle={() => toggleGroup('groups')}
        error={doc.groupError}
      />
      <RGroupToolGroup
        tool={tool}
        setTool={setTool}
        usedLabels={doc.markushUsedLabels}
        newLabel={doc.markushNewLabel}
        {...(onOpenAtomList ? { onOpenAtomList } : {})}
        {...(bracket ? { bracket } : {})}
        open={openGroup === 'markush'}
        onToggle={() => toggleGroup('markush')}
      />
      <ReactionArrowsGroup
        tool={tool}
        setTool={setTool}
        open={openGroup === 'reactionArrows'}
        onToggle={() => toggleGroup('reactionArrows')}
        schemeArrowPlaced={doc.hasReactionArrow}
      />
      <ShapesGroup tool={tool} setTool={setTool} open={openGroup === 'shapes'} onToggle={() => toggleGroup('shapes')} />
      <CalloutsGroup
        tool={tool}
        setTool={setTool}
        open={openGroup === 'callouts'}
        onToggle={() => toggleGroup('callouts')}
      />
      <TemplatesRail
        doc={doc}
        onLoad={onLoadTemplate}
        onLoadMolecule={onLoadMoleculeTemplate}
        loadDisabled={templateLoadDisabled}
        open={openGroup === 'templates'}
        onToggle={() => toggleGroup('templates')}
      />
    </aside>
  );
}
